import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Plus, Edit, Trash2, Shield, Eye, Lock } from 'lucide-react';

const mockRoles = [
  {
    id: '1',
    name: 'Super Admin',
    description: 'Full system access with all permissions',
    permissions: ['read', 'write', 'delete', 'admin', 'system'],
    userCount: 2,
    type: 'system'
  },
  {
    id: '2',
    name: 'Workspace Admin',
    description: 'Manage workspace settings and users',
    permissions: ['read', 'write', 'admin'],
    userCount: 5,
    type: 'workspace'
  },
  {
    id: '3',
    name: 'Editor',
    description: 'Edit content and manage bills',
    permissions: ['read', 'write'],
    userCount: 12,
    type: 'workspace'
  },
  {
    id: '4',
    name: 'Viewer',
    description: 'Read-only access to workspace content',
    permissions: ['read'],
    userCount: 23,
    type: 'workspace'
  },
  {
    id: '5',
    name: 'Analyst',
    description: 'Access to reports and analytics',
    permissions: ['read', 'analytics'],
    userCount: 8,
    type: 'custom'
  }
];

const availablePermissions = [
  { id: 'read', name: 'Read', description: 'View content and data' },
  { id: 'write', name: 'Write', description: 'Create and edit content' },
  { id: 'delete', name: 'Delete', description: 'Remove content and data' },
  { id: 'admin', name: 'Admin', description: 'Administrative functions' },
  { id: 'analytics', name: 'Analytics', description: 'Access reports and analytics' },
  { id: 'system', name: 'System', description: 'System-level access' },
];

const mockUsers = [
  { id: '1', name: 'John Doe', email: 'john@acme.com', role: 'Super Admin', status: 'active' },
  { id: '2', name: 'Jane Smith', email: 'jane@acme.com', role: 'Workspace Admin', status: 'active' },
  { id: '3', name: 'Bob Johnson', email: 'bob@acme.com', role: 'Editor', status: 'active' },
  { id: '4', name: 'Alice Brown', email: 'alice@acme.com', role: 'Viewer', status: 'pending' },
];

const getRoleTypeColor = (type: string) => {
  switch (type) {
    case 'system':
      return 'bg-purple-100 text-purple-800';
    case 'workspace':
      return 'bg-blue-100 text-blue-800';
    case 'custom':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getPermissionIcon = (permission: string) => {
  switch (permission) {
    case 'read':
      return <Eye className="h-3 w-3" />;
    case 'write':
      return <Edit className="h-3 w-3" />;
    case 'delete':
      return <Trash2 className="h-3 w-3" />;
    case 'admin':
      return <Shield className="h-3 w-3" />;
    case 'system':
      return <Lock className="h-3 w-3" />;
    default:
      return <Eye className="h-3 w-3" />;
  }
};

export default function RoleManagement() {
  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Role Management</h1>
            <p className="text-muted-foreground">
              Manage user roles and permissions across your workspace
            </p>
          </div>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
              <DialogDescription>
                Define a new role with specific permissions for your workspace.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Role Name</label>
                <Input placeholder="Enter role name" />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input placeholder="Describe this role" />
              </div>
              <div>
                <label className="text-sm font-medium">Permissions</label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {availablePermissions.map((permission) => (
                    <div key={permission.id} className="flex items-center space-x-2 border rounded p-3">
                      <input type="checkbox" id={permission.id} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {getPermissionIcon(permission.id)}
                          <label htmlFor={permission.id} className="text-sm font-medium cursor-pointer">
                            {permission.name}
                          </label>
                        </div>
                        <p className="text-xs text-muted-foreground">{permission.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsCreateDialogOpen(false)}>
                  Create Role
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockRoles.length}</div>
            <p className="text-xs text-muted-foreground">
              Active roles
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockRoles.reduce((sum, role) => sum + role.userCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Assigned users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custom Roles</CardTitle>
            <Plus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockRoles.filter(role => role.type === 'custom').length}
            </div>
            <p className="text-xs text-muted-foreground">
              User-defined
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Roles</CardTitle>
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockRoles.filter(role => role.type === 'system').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Built-in roles
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <CardDescription>
            Manage roles and their permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockRoles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{role.name}</div>
                      <div className="text-sm text-muted-foreground">{role.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getRoleTypeColor(role.type)}>
                      {role.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.map((permission) => (
                        <Badge key={permission} variant="outline" className="text-xs">
                          <div className="flex items-center gap-1">
                            {getPermissionIcon(permission)}
                            {permission}
                          </div>
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{role.userCount} users</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      {role.type === 'custom' && (
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Role Assignments */}
      <Card>
        <CardHeader>
          <CardTitle>User Role Assignments</CardTitle>
          <CardDescription>
            View and manage user role assignments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <Select value={selectedRole || 'all'} onValueChange={(value) => setSelectedRole(value === 'all' ? undefined : value)}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {mockRoles.map((role) => (
                    <SelectItem key={role.id} value={role.name}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Assign Role
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockUsers
                  .filter(user => !selectedRole || user.role === selectedRole)
                  .map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}